/**
 * The GPU half of runtime ramp re-shading: a TSL node material that redoes the
 * original's draw-time shading step every frame, so a face's colour walks its
 * `BARON.PAL` ramp as its aeroplane banks. `ramp-shade.ts` holds the rules and
 * the buffers; this file is the three.js/WebGPU plumbing around them, in the
 * same split `screen-sprites.ts` uses.
 *
 * **Two 8-bit lookups, no lighting model.** The shader fetches the biased
 * signed delta for `(shade level, half-width - 1)` out of a 32x8 texture, adds
 * it to the face's baked anchor index, and fetches that palette entry out of a
 * 256x1 texture. Both are `NearestFilter` with no mipmaps: a palette index is
 * not a quantity to interpolate, and a filtered fetch would blend two ramp
 * steps into a colour the original cannot draw. The result is the original's
 * banded walk rather than a smooth Lambert term (see `ramp-shade.ts`).
 *
 * **Why the *world* normal.** The engine dots the sun with model-space normals
 * after rotating it into the object's frame; a rotation preserves the dot, so
 * `dot(worldNormal, SUN_DIRECTION_WORLD)` is the same number and costs the
 * shader no knowledge of the pose. It also makes the material indifferent to
 * *where* the -90° model-frame fix was applied — baked into the geometry
 * (`world-instancer.ts`) or carried on the object matrix (`aircraft-mesh.ts`) —
 * because both end up in the same world frame.
 *
 * **Two-sided faces flip.** §3: "for back-facing two-sided polygons the engine
 * negates the normal before the dot product", so the reverse side gets its own,
 * opposite shade. `frontFacing` is exactly that test, and the material is
 * `DoubleSide` for the same reason the plain one is.
 *
 * A face the engine never shades (a zero `shadeHalfWidth`) keeps its baked
 * vertex colour untouched — which is every scenery face, every flat colour, and
 * the three blacks. The one exception is the material 3D COCKPIT hands an
 * interior: from a metre away a flat strut is a dead slab rather than a detail
 * too small to shade, so that one material walks a **recovered** ramp for those
 * faces instead (`structure-ramp.ts`, `flight/cockpit-interior.ts`).
 *
 * **Colour space: the palette texture is `NoColorSpace`, and now so is
 * everything else.** This app feeds `BARON.PAL`'s 8-bit values into the
 * `color` vertex attribute as `byte / 255`, and three treats a vertex colour
 * as already being in the working (linear) space — so left to three's
 * defaults, the output pass would encode it to sRGB and every baked model in
 * `/fly` would draw *brighter* than the palette entry it came from (measured
 * on a WebGPU readback: index 18 `166,130,52` reached the framebuffer as
 * `211,189,125`). Tagging only this lookup `SRGBColorSpace` would have made
 * the shaded faces land on the true palette byte while the flat ones stayed
 * brightened — two tones on one aeroplane — which is why this was left raw
 * and filed as the app-wide question `webgpu-support.ts` now answers:
 * `ColorManagement.enabled = false`, set once for every three.js surface in
 * the app, so nothing anywhere sRGB-encodes or -decodes and this texture's
 * own `NoColorSpace` tag is now one documented instance of a rule applied
 * everywhere rather than a local workaround.
 */

import * as THREE from "three/webgpu";
import {
  paletteTexels,
  shadeDeltaTexels,
  PALETTE_ENTRY_COUNT,
  SHADE_DELTA_BIAS,
  SUN_DIRECTION_WORLD,
  type BakedPaletteBody,
} from "./ramp-shade.ts";
import { SHADE_LEVEL_COUNT, SHADE_ROW_COUNT } from "@red-opus/formats";
import type { CockpitInterior } from "./cockpit-interior.ts";
import { createStructureRampTexture } from "./structure-ramp.ts";

/** The palette the sim draws its 3D with — `BARON.PAL`, identical to `FIGHTER.PAL` (shading-and-paint.md §6). */
export const SHADING_PALETTE_PATH = "/baked/palettes/BARON.PAL.json";

/** Geometry attribute names the material reads — see `ramp-shade.ts`'s `shadeRampAttributes`. */
export const SHADE_ANCHOR_ATTRIBUTE = "shadeAnchor";
export const SHADE_HALF_WIDTH_ATTRIBUTE = "shadeHalfWidth";

/** `shadeLevelFromCosine`'s quantiser, as the shader's own constant: `floor(16·cos)` clamped to `-16..15`, biased by +16. */
const SHADE_LEVEL_SCALE = 16;

function dataTexture(data: Uint8Array, width: number, height: number, format: THREE.PixelFormat): THREE.DataTexture {
  const texture = new THREE.DataTexture(data, width, height, format);
  texture.magFilter = THREE.NearestFilter;
  texture.minFilter = THREE.NearestFilter;
  texture.generateMipmaps = false;
  texture.needsUpdate = true;
  return texture;
}

/**
 * The lookup textures a ramp material walks. All are constants of the *game*,
 * not of any one scene — 8 KB for the shipped pair and 32 KB for the recovered
 * third — so a caller is meant to build one set and hand it to every material it
 * makes (see `flight/aircraft-mesh.ts` for the ownership note).
 */
export interface RampTables {
  readonly palette: THREE.DataTexture;
  readonly deltas: THREE.DataTexture;
  /**
   * A recovered ramp per palette index, for the entries `SHADES.DAT` marks flat
   * (`structure-ramp.ts`). Read by **one** material in the whole app — the
   * player's own aeroplane under 3D COCKPIT — and built with the other two so
   * a caller still owns one set.
   */
  readonly structure: THREE.DataTexture;
}

export function createRampTables(palette: BakedPaletteBody): RampTables {
  const paletteTexture = dataTexture(paletteTexels(palette), PALETTE_ENTRY_COUNT, 1, THREE.RGBAFormat);
  // `NoColorSpace`, deliberately — see the module doc's colour-space note. This
  // hands the shader the raw `byte / 255` that `model-geometry.ts` puts in the
  // `color` attribute, so a face that walks its ramp and a face that keeps its
  // baked colour go through *identical* handling from here on.
  paletteTexture.colorSpace = THREE.NoColorSpace;
  const deltaTexture = dataTexture(shadeDeltaTexels(), SHADE_LEVEL_COUNT, SHADE_ROW_COUNT, THREE.RedFormat);
  return { palette: paletteTexture, deltas: deltaTexture, structure: createStructureRampTexture(palette) };
}

/**
 * `dot(worldNormal, sun)`, with the normal negated on a back face — the engine's
 * "flip normal" for two-sided polygons.
 */
function lambertCosine(): THREE.Node<"float"> {
  const sun = THREE.TSL.vec3(...SUN_DIRECTION_WORLD);
  const facing = THREE.TSL.faceDirection;
  return THREE.TSL.normalWorld.mul(facing).dot(sun);
}

/** Texel-centre UV for integer coordinate `index` in a lookup of `count` texels. */
function texelUv(index: THREE.Node<"float">, count: number): THREE.Node<"float"> {
  return index.add(0.5).div(count);
}

/**
 * The colour node: the baked vertex colour for a flat face, the ramp walk for a
 * shaded one. Written as a `select` rather than a branch so both sides are
 * uniform-cost — the walk is two texture fetches either way and a divergent
 * branch would buy nothing.
 */
function rampColorNode(tables: RampTables, interior: CockpitInterior | undefined): THREE.Node<"vec3"> {
  const anchor = THREE.TSL.attribute<"float">(SHADE_ANCHOR_ATTRIBUTE, "float");
  const halfWidth = THREE.TSL.attribute<"float">(SHADE_HALF_WIDTH_ATTRIBUTE, "float");

  // 3D COCKPIT's structure treatment, where a caller asked for one: this walk
  // keeps its own cosine and only gains the muzzle-flash term, while the *flat*
  // branch below gains a recovered ramp (`three/cockpit-interior.ts`). With no
  // interior both are the identity and the shader is unchanged, which is every
  // aeroplane in the sky but the player's own.
  const base = lambertCosine();
  const cosine = interior ? interior.cosineNode(base) : base;
  const level = shadeLevel(cosine);
  const deltaUv = THREE.TSL.vec2(texelUv(level, SHADE_LEVEL_COUNT), texelUv(halfWidth.sub(1), SHADE_ROW_COUNT));
  // The texture holds the signed delta biased by +8 and normalised to 0..1.
  const delta = THREE.TSL.texture(tables.deltas, deltaUv).r.mul(255).round().sub(SHADE_DELTA_BIAS);

  const index = anchor.add(delta);
  const shaded = THREE.TSL.texture(tables.palette, THREE.TSL.vec2(texelUv(index, PALETTE_ENTRY_COUNT), 0.5)).rgb;
  return THREE.TSL.select(halfWidth.greaterThan(0), shaded, flatColorNode(tables, interior, anchor));
}

/** `level = clamp(floor(16·cos), -16, 15) + 16`, i.e. 0..31 — `shadeLevelFromCosine` as a node. */
function shadeLevel(cosine: THREE.Node<"float">): THREE.Node<"float"> {
  return cosine.mul(SHADE_LEVEL_SCALE).floor().clamp(-SHADE_LEVEL_SCALE, SHADE_LEVEL_SCALE - 1).add(SHADE_LEVEL_SCALE);
}

/**
 * What a face `SHADES.DAT` marks **flat** draws.
 *
 * Its baked vertex colour, as it always has been — except on the one material
 * 3D COCKPIT hands an interior, where it walks a *recovered* ramp instead
 * (`structure-ramp.ts`). That is the whole of the structure treatment, and it
 * exists because the faces the pilot's own frame is made of — the cabane
 * struts, the bracing, the undercarriage, all `BARON.PAL` 120 on a Camel — are
 * flat by the game's own table, so the walk above never reaches them and no
 * cosine anyone computes can move them.
 *
 * `mix` on the gate rather than a `select`, so with the setting off the result
 * is the vertex colour **exactly** — the "off is today's pixels" contract.
 */
function flatColorNode(tables: RampTables, interior: CockpitInterior | undefined, anchor: THREE.Node<"float">): THREE.Node<"vec3"> {
  const baked = THREE.TSL.vertexColor().rgb;
  if (!interior) return baked;
  const level = shadeLevel(interior.structureCosineNode());
  const uv = THREE.TSL.vec2(texelUv(anchor, PALETTE_ENTRY_COUNT), texelUv(level, SHADE_LEVEL_COUNT));
  return THREE.TSL.mix(baked, THREE.TSL.texture(tables.structure, uv).rgb, interior.enabledNode());
}

/**
 * An unlit, vertex-coloured material that re-shades every face the engine
 * shades. Drop-in for the `MeshBasicMaterial({ vertexColors: true, side:
 * DoubleSide })` the model paths used before, and it falls back to exactly that
 * material's output for any mesh whose geometry carries no `shadeRamp`
 * attribute value above zero.
 */
export function createRampMaterial(tables: RampTables, interior?: CockpitInterior): THREE.MeshBasicNodeMaterial {
  const material = new THREE.MeshBasicNodeMaterial({ side: THREE.DoubleSide });
  material.colorNode = rampColorNode(tables, interior);
  return material;
}
